import '@babel/polyfill';
import 'classlist.js';
import 'es6-promise/auto';
import 'es6-object-assign/auto';
import './fontawesome';
import { createApp } from 'vue';
import Store from './store';
import Router from './router';
import VueMixins from './vue/mixins';
import VTooltip from 'v-tooltip';
import VueCookie from './utils/vueCookie';
import ToggleButton from 'vue-js-toggle-button';
import AppRoot from './views/App/AppRoot.vue';
import VueSocialSharing from 'vue-social-sharing/src/vue-social-sharing';
import Buefy from 'buefy/src';
import VueCroppie from 'vue-croppie/src';
import PortalVue from 'portal-vue';
import newrelicLogger from './utils/newrelic-logger';
import DesignV2API from './utils/themeStyles';
import 'croppie/croppie.css';
import { VueHammer } from 'vue2-hammer';
import { loggerVue } from 'services/logger';
import { heartbeat } from 'utils/heartbeat';
import { detectWakeMode } from 'utils/detectWakeMode';
import VueSuperMethod from 'vue-super-call';
import { initSentry } from 'core/Sentry';
import { GlobalComponents } from './vue/components';
import { FontAwesomeComponents } from './fontawesome';
import modules from 'store/modules';
import filters from './vue/filters';
import { GlobalDirective } from './vue/directive';
import { eventRoot } from 'utils/eventRoot';

/**
 * Bootstrap jQuery, Bootstrap, Stripe, etc.
 *
 * // TODO everything in this file should be some sort of ES6 module
 *         at some point.
 */
require('./bootstrap');

export const app = createApp(AppRoot);

initSentry(app);

app.use(Store);
app.use(Router);

/**
 * First thing to do is bootstrap Vue itself.
 */
app.mixin(VueMixins);
app.config.devtools = process.env.NODE_ENV !== 'production';

app.use(VTooltip, {
    defaultHtml: true,
    defaultContainer: 'body',
    autoHide: false,
    disposeTimeout: 100,
});
app.use(ToggleButton);
app.use(VueCroppie);
app.use(VueSocialSharing);
app.use(Buefy, {
    defaultIconComponent: 'font-awesome-icon',
    defaultIconPack: 'fas',
});
app.use(VueHammer);
app.use(PortalVue);

/**
 * global config
 */
app.config.globalProperties.$set = (data, key, voice) => {
    data[key] = voice;
};
app.config.globalProperties.$rootEvents = eventRoot;

app.config.globalProperties.$filters = filters;
app.config.globalProperties.$cookie = VueCookie;
app.config.globalProperties.$log = loggerVue;
app.config.globalProperties.appConfig = window.Config;

app.config.globalProperties.$super = VueSuperMethod;
app.config.globalProperties.$isLocal = window.Config.env === 'local';
app.config.globalProperties.$isQa = window.Config.env === 'qa';
app.config.globalProperties.$isStaging = window.Config.env === 'staging';
app.config.globalProperties.$isProd = window.Config.env === 'production';
app.config.globalProperties.$isRecaptcha = window.Config.is_recaptcha;
app.config.globalProperties.$window = window;
app.config.globalProperties.$debugNR = newrelicLogger;
app.config.globalProperties.$delay = (time = 1000) => {
    return new Promise((resolve) => {
        setTimeout(() => {
            return resolve();
        }, time);
    });
};

const $storeData = (() => {
    let data = {
        actions: {},
        getters: {},
    };

    for (const storeKey in modules) {
        const store = modules[storeKey]();

        const actionsKeys = Object.getOwnPropertyNames(store)
            .filter((prop) => typeof store[prop] === 'function')
            .filter((prop) => !prop.startsWith('$'));

        const actionsKeysData = actionsKeys.reduce((state, actionKey) => {
            state[actionKey] = storeKey;

            return state;
        }, {});

        const gettersKeys = Object.keys(store).filter((key) => {
            return (
                !(typeof store[key] === 'function') &&
                !key.startsWith('$') &&
                !key.startsWith('_') &&
                key !== '$state'
            );
        });
        const gettersKeysData = gettersKeys.reduce((state, getterKey) => {
            state[getterKey] = storeKey;

            return state;
        }, {});

        data = {
            actions: {
                ...data.actions,
                ...actionsKeysData,
            },
            getters: {
                ...data.getters,
                ...gettersKeysData,
            },
        };
    }

    return data;
})();
app.config.globalProperties.$store = {
    dispatch: (name, args) => {
        if (!$storeData.actions[name]) return console.error(`not found action - ${name}`);

        const storeKey = $storeData.actions[name];
        const actionKey = name;

        return modules[storeKey]()[actionKey](args);
    },

    get getters() {
        return Object.entries($storeData.getters).reduce((state, [getterKey, storeKey]) => {
            state[getterKey] = modules[storeKey]()[getterKey];

            return state;
        }, {});
    },
};

/**
 * Run Heartbeat
 */

/**
 * Detect Wake Mode
 */
detectWakeMode.init();

/**
 * Finally, we will boot our application.
 */
window.RouterVue = Router;
window.DesignV2API = new DesignV2API();

GlobalComponents(app);
GlobalDirective(app);
FontAwesomeComponents(app);
heartbeat(true);

app.mount('#app');
